import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Briefcase, CalendarDays, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PrimaryButton, Reveal, SectionTag, TechTag } from "@/components/ui-bits";
import api, { formatApiError } from "@/lib/api";

const inputCls = "w-full rounded-lg border border-tark-line bg-tark-bg px-4 py-3 text-sm text-tark-ink placeholder:text-tark-dim focus:border-tark-cyan/60 focus:outline-none focus:ring-1 focus:ring-tark-cyan/40";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-tark-dim";

const Field = ({ label, name, value, onChange, type = "text", required = false, placeholder = "", testId }) => (
  <div>
    <label htmlFor={name} className={labelCls}>{label}{required && " *"}</label>
    <input id={name} name={name} type={type} value={value} onChange={onChange} required={required}
           placeholder={placeholder} className={inputCls} data-testid={testId || `apply-${name}`} />
  </div>
);

const ApplicationForm = ({ job }) => {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", current_location: "",
    total_experience: "", relevant_experience: "", current_company: "",
    current_ctc: "", expected_ctc: "", notice_period: "",
    linkedin_profile: "", portfolio_github: "", cover_note: "",
  });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, position_applied_for: job.title, job_id: job.id }).forEach(([k, v]) => fd.append(k, v));
      if (resume) fd.append("resume", resume);
      await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Application submitted successfully.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-tark-cyan/40 bg-tark-card p-10 text-center" data-testid="application-success">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-tark-cyan/40 bg-tark-cyan/10">
          <span className="h-3 w-3 rotate-45 bg-tark-cyan" />
        </div>
        <h3 className="mt-6 font-display text-xl font-bold">Application received</h3>
        <p className="mx-auto mt-3 max-w-sm text-sm text-tark-mut">
          Thank you for applying to TARK. Our talent team will review your profile and reach out if there is a match.
        </p>
        <Link to="/careers" data-testid="application-back-link" className="mt-6 inline-block font-display text-sm font-bold text-tark-cyan hover:underline">
          Back to all openings
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-tark-line bg-tark-card p-8 sm:p-10" data-testid="application-form">
      <h3 className="font-display text-xl font-bold tracking-tight">Apply for This Position</h3>
      <p className="mt-2 text-sm text-tark-mut">{job.title} · {job.department}</p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <Field label="Full Name" name="full_name" value={form.full_name} onChange={set} required />
        <Field label="Email" name="email" type="email" value={form.email} onChange={set} required />
        <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={set} required />
        <Field label="Current Location" name="current_location" value={form.current_location} onChange={set} />
        <div>
          <label htmlFor="position" className={labelCls}>Position Applied For</label>
          <input id="position" value={job.title} readOnly className={`${inputCls} opacity-70`} data-testid="apply-position" />
        </div>
        <Field label="Total Experience" name="total_experience" value={form.total_experience} onChange={set} placeholder="e.g. 5 years" />
        <Field label="Relevant Experience" name="relevant_experience" value={form.relevant_experience} onChange={set} placeholder="e.g. 3 years" />
        <Field label="Current Company" name="current_company" value={form.current_company} onChange={set} />
        <Field label="Current CTC" name="current_ctc" value={form.current_ctc} onChange={set} />
        <Field label="Expected CTC" name="expected_ctc" value={form.expected_ctc} onChange={set} />
        <Field label="Notice Period" name="notice_period" value={form.notice_period} onChange={set} placeholder="e.g. 30 days" />
        <Field label="LinkedIn Profile" name="linkedin_profile" value={form.linkedin_profile} onChange={set} placeholder="https://linkedin.com/in/…" />
        <Field label="Portfolio / GitHub" name="portfolio_github" value={form.portfolio_github} onChange={set} placeholder="https://github.com/…" />
        <div>
          <label htmlFor="resume" className={labelCls}>Resume Upload (PDF / DOC)</label>
          <input id="resume" type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResume(e.target.files?.[0] || null)}
                 className="w-full rounded-lg border border-dashed border-tark-line bg-tark-bg px-4 py-3 text-sm text-tark-mut file:mr-4 file:rounded-full file:border-0 file:bg-tark-blue file:px-4 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-tark-vivid"
                 data-testid="apply-resume" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cover_note" className={labelCls}>Cover Note</label>
          <textarea id="cover_note" name="cover_note" rows={4} value={form.cover_note} onChange={set}
                    className={inputCls} placeholder="Tell us why this role, why TARK."
                    data-testid="apply-cover-note" />
        </div>
      </div>

      <div className="mt-8">
        <PrimaryButton type="submit" testId="apply-submit" onClick={undefined}>
          {submitting ? "Submitting…" : "Submit Application"}
        </PrimaryButton>
      </div>
    </form>
  );
};

const DetailBlock = ({ title, items }) =>
  items?.length > 0 && (
    <div className="mt-10">
      <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
      <ul className="mt-4 space-y-2.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-tark-mut">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-tark-cyan" />{item}
          </li>
        ))}
      </ul>
    </div>
  );

const JobDetail = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/jobs/${id}`)
      .then((r) => setJob(r.data))
      .catch(() => setJob(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex min-h-screen items-center justify-center font-mono text-sm text-tark-dim">Loading…</div>;
  if (!job) return <Navigate to="/careers" replace />;

  return (
    <section className="relative overflow-hidden pt-40 pb-28" data-testid="job-detail">
      <div className="absolute inset-0 spotlight" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Link to="/careers" data-testid="job-back-link" className="inline-flex items-center gap-2 font-mono text-xs text-tark-dim transition-colors hover:text-tark-cyan">
            <ArrowLeft className="h-3.5 w-3.5" /> All Openings
          </Link>

          <div className="mt-8">
            <SectionTag>{job.department}</SectionTag>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">{job.title}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-xs text-tark-dim">
              {job.location && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{job.location}</span>}
              {job.experience && <span className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" />{job.experience}</span>}
              {job.work_mode && <span className="rounded border border-tark-line px-2 py-0.5">{job.work_mode}</span>}
              {job.employment_type && <span className="rounded border border-tark-line px-2 py-0.5">{job.employment_type}</span>}
              {job.application_deadline && (
                <span className="flex items-center gap-1.5 text-tark-cyan"><CalendarDays className="h-3.5 w-3.5" />Apply by {job.application_deadline}</span>
              )}
            </div>
          </div>
        </motion.div>

        <div className="mt-14 grid gap-10 lg:grid-cols-2">
          <Reveal>
            {job.description && <p className="text-base leading-relaxed text-tark-mut">{job.description}</p>}
            {job.required_skills?.length > 0 && (
              <div className="mt-8">
                <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-tark-ink">Required Skills</h3>
                <div className="mt-4 flex flex-wrap gap-2.5" data-testid="job-required-skills">
                  {job.required_skills.map((s) => <TechTag key={s}>{s}</TechTag>)}
                </div>
              </div>
            )}
            <DetailBlock title="Responsibilities" items={job.responsibilities} />
            <DetailBlock title="Qualifications" items={job.qualifications} />
            <DetailBlock title="Preferred Skills" items={job.preferred_skills} />
          </Reveal>
          <Reveal delay={0.1}>
            <ApplicationForm job={job} />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default JobDetail;
